import { useState, useMemo } from "react";
import { useFacilities } from "../../hooks/useFacilities";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Textarea,
  Alert,
  Checkbox,
  Badge,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  AUDIT_SCOPE_LABELS,
  AUDIT_SCOPE_DISCLOSURE,
} from "@livingsyncs/types";
import { AuditScopeContents } from "./AuditScopeContents";

import { notifications } from "@mantine/notifications";
import { IconCheck, IconX, IconAlertCircle } from "@tabler/icons-react";
import {
  useApproveAuditRequest,
  useDenyAuditRequest,
} from "../../hooks/useAuditRequests";
import type { AccessScope, AuditAccessRequest } from "../../types/audit";
import { extractApiError } from "../../utils/formErrorHandler";
import { parseDisplayDate, withheldScopes } from "@livingsyncs/types";
import { formatDateNumeric } from "../../utils/formatters";


/** `@db.Date` / ISO string → YYYY-MM-DD, the format the bound predicates use. */
const dateOnly = (v: string) => v.slice(0, 10);

/**
 * A picker bound, built as a LOCAL calendar day (US-151 / F-082).
 *
 * `new Date(request.requested_end_date)` is UTC midnight, and Mantine compares a
 * bound via `dayjs(bound).format("YYYY-MM-DD")` in LOCAL time — so in every zone
 * behind UTC the bound landed on the PREVIOUS day and the approver could not
 * select the date the auditor had asked for. The grant then stored a day less
 * access than both parties agreed, invisibly, because every label rendered
 * through the same shift.
 */
const bound = (v: string | null | undefined): Date | undefined =>
  parseDisplayDate(v ? dateOnly(v) : null) ?? undefined;

interface AuditRequestApprovalModalProps {
  request: AuditAccessRequest | null;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for managers to approve or deny audit access requests
 */
export function AuditRequestApprovalModal({
  request,
  opened,
  onClose,
}: AuditRequestApprovalModalProps) {
  // F-132: names for the request's facility ids. Shares the TanStack cache
  // with the parent tab's own useFacilities call, so this costs no extra fetch.
  const { data: facilities } = useFacilities();
  const facilityNameById = useMemo(
    () => new Map((facilities ?? []).map((f) => [f.id, f.name])),
    [facilities],
  );

  const [mode, setMode] = useState<"approve" | "deny" | null>(null);
  const [validFrom, setValidFrom] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState("");
  // US-140 (AC20/AC22): this modal never sent access_scopes at all, and the
  // server defaulted the omission to ALL FIVE scopes — so every real grant
  // carried full PHI access regardless of what was requested. The approver now
  // chooses explicitly, seeded with exactly what the auditor asked for.
  const [grantedScopes, setGrantedScopes] = useState<AccessScope[] | null>(
    null,
  );

  const requestedScopes: AccessScope[] = request?.requested_scopes ?? [];
  // `null` = untouched, so seed from the request; [] = approver cleared it all.
  const effectiveScopes = grantedScopes ?? requestedScopes;
  const withheld = request
    ? withheldScopes(
        {
          requested_scopes: requestedScopes,
          requested_start_date: dateOnly(request.requested_start_date),
          requested_end_date: dateOnly(request.requested_end_date),
        },
        {
          access_scopes: effectiveScopes,
          valid_from: validFrom ?? "",
          valid_until: validUntil ?? "",
        },
      )
    : [];

  const approveRequest = useApproveAuditRequest(request?.id || "");
  const denyRequest = useDenyAuditRequest(request?.id || "");

  const handleApprove = async () => {
    if (!validFrom || !validUntil) {
      notifications.show({
        title: "Error",
        message: "Please select valid from and until dates",
        color: "red",
      });
      return;
    }

    if (validUntil < validFrom) {
      notifications.show({
        title: "Error",
        message: "Valid until date must be after valid from date",
        color: "red",
      });
      return;
    }

    if (effectiveScopes.length === 0) {
      notifications.show({
        title: "Error",
        message: "Select at least one data scope to grant",
        color: "red",
      });
      return;
    }

    try {
      await approveRequest.mutateAsync({
        valid_from: validFrom,
        valid_until: validUntil,
        access_scopes: effectiveScopes,
      });

      notifications.show({
        title: "Success",
        message: "Audit request approved successfully",
        color: "green",
      });

      handleClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: extractApiError(error, "Failed to approve request"),
        color: "red",
      });
    }
  };

  const handleDeny = async () => {
    if (!denialReason || denialReason.length < 10) {
      notifications.show({
        title: "Error",
        message: "Please provide a denial reason (minimum 10 characters)",
        color: "red",
      });
      return;
    }

    try {
      await denyRequest.mutateAsync({
        denial_reason: denialReason,
      });

      notifications.show({
        title: "Success",
        message: "Audit request denied",
        color: "green",
      });

      handleClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: extractApiError(error, "Failed to deny request"),
        color: "red",
      });
    }
  };

  const handleClose = () => {
    setMode(null);
    setValidFrom(null);
    setValidUntil(null);
    setGrantedScopes(null);
    setDenialReason("");
    onClose();
  };

  if (!request) return null;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Review Audit Request"
      size="lg"
    >
      <Stack gap="md">
        {/* Request Details */}
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {request.auditor_organization}
            </Text>
            <Text size="sm">Type: {request.audit_type.replace("_", " ")}</Text>
            {/* US-177 (F-132): this used to read "Facilities: 2".
                The approver is authorising an outside party to read residents'
                records, and a COUNT does not tell them whether the request
                covers the home they manage or a different one. The ids are on
                the row; the names come from the same useFacilities query the
                parent tab already runs, so this is a rendering gap, not a
                missing join.
                An id that cannot be resolved is shown as unresolved rather than
                dropped — silently omitting a facility would understate the
                scope of what is being approved, which is the same defect. */}
            <Text size="sm">
              Facilities ({request.facility_ids.length}):{" "}
              {request.facility_ids.length === 0
                ? "none specified"
                : request.facility_ids
                    .map((id) => facilityNameById.get(id) ?? "Unknown facility")
                    .join(", ")}
            </Text>
            <Text size="sm">
              Requested period:{" "}
              {formatDateNumeric(request.requested_start_date)} -{" "}
              {formatDateNumeric(request.requested_end_date)}
            </Text>
            <Group gap={4} align="center">
              <Text size="sm">Requested scopes:</Text>
              {requestedScopes.length === 0 ? (
                <Text size="sm" c="dimmed" fs="italic">
                  none stated (request predates scoped requests)
                </Text>
              ) : (
                requestedScopes.map((s) => (
                  <Badge key={s} size="sm" variant="light">
                    {AUDIT_SCOPE_LABELS[s]}
                  </Badge>
                ))
              )}
            </Group>
          </Stack>
        </Alert>

        <div>
          <Text size="sm" fw={500} mb="xs">
            Audit Purpose:
          </Text>
          <Text size="sm" c="dimmed">
            {request.audit_purpose}
          </Text>
        </div>

        {!mode && (
          <Group justify="apart" mt="md">
            <Button
              color="red"
              leftSection={<IconX size={16} />}
              onClick={() => setMode("deny")}
            >
              Deny
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={() => setMode("approve")}
            >
              Approve
            </Button>
          </Group>
        )}

        {mode === "approve" && (
          <Stack gap="md">
            <Text size="sm" fw={500}>
              Set Access Period
            </Text>
            {/* US-140 (AC21): the grant may narrow the requested window, never
                widen it, so the pickers are bounded by the request itself —
                the server enforces the same rule and 400s a widening. */}
            <Group grow>
              <DateInput
                label="Valid From"
                placeholder="Select start date"
                value={validFrom}
                onChange={setValidFrom}
                minDate={bound(request.requested_start_date)}
                maxDate={bound(request.requested_end_date)}
                required
              />
              <DateInput
                label="Valid Until"
                placeholder="Select end date"
                value={validUntil}
                onChange={setValidUntil}
                minDate={bound(validFrom ?? request.requested_start_date)}
                maxDate={bound(request.requested_end_date)}
                required
              />
            </Group>

            {/* US-140 (AC20/AC22): explicit scope grant, seeded from the request.
                Only requested scopes are offered — granting more than was asked
                for needs a new request, not a wider approval. */}
            <div>
              <Text size="sm" fw={500} mb={4}>
                Data Scopes Granted
              </Text>
              {requestedScopes.length === 0 ? (
                <Text size="sm" c="dimmed">
                  This request predates scoped requests, so there is nothing to
                  narrow. Choose deliberately — nothing here is implied.
                </Text>
              ) : (
                <Text size="xs" c="dimmed" mb="xs">
                  Uncheck anything this audit does not need. You cannot add a
                  scope the auditor did not request.
                </Text>
              )}
              <Stack gap={4}>
                {(requestedScopes.length > 0
                  ? requestedScopes
                  : (Object.keys(AUDIT_SCOPE_LABELS) as AccessScope[])
                ).map((scope) => (
                  <Checkbox
                    key={scope}
                    label={AUDIT_SCOPE_LABELS[scope]}
                    // US-180/F-099: what this scope hands over, at the moment
                    // of authorising it — the approver used to see only a name.
                    description={AUDIT_SCOPE_DISCLOSURE[scope]?.summary}
                    checked={effectiveScopes.includes(scope)}
                    onChange={(e) =>
                      setGrantedScopes(
                        e.currentTarget.checked
                          ? [...effectiveScopes, scope]
                          : effectiveScopes.filter((s) => s !== scope),
                      )
                    }
                  />
                ))}
              </Stack>
              <AuditScopeContents scopes={effectiveScopes} />
              {withheld.length > 0 && (
                <Alert
                  mt="xs"
                  color="yellow"
                  icon={<IconAlertCircle size={16} />}
                >
                  Narrowing this request — withholding{" "}
                  {withheld.map((s) => AUDIT_SCOPE_LABELS[s]).join(", ")}. The auditor
                  will not be able to see that data.
                </Alert>
              )}
            </div>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setMode(null)}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleApprove}
                loading={approveRequest.isPending}
              >
                Confirm Approval
              </Button>
            </Group>
          </Stack>
        )}

        {mode === "deny" && (
          <Stack gap="md">
            <Textarea
              label="Denial Reason"
              placeholder="Explain why this request is being denied..."
              description="Minimum 10 characters"
              value={denialReason}
              onChange={(e) => setDenialReason(e.target.value)}
              minRows={4}
              required
            />

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setMode(null)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeny}
                loading={denyRequest.isPending}
              >
                Confirm Denial
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
